import { nameColumn } from "../../../layouts/Table/columns/nameColumn";
import dateColumn from "../../../layouts/Table/columns/dateColumn";
import TableLayoutBlock from "../../../layouts/Table/TableLayoutBlock";
import { useLanguage } from "../../../stores/LanguageStore";
import { useCallback, useMemo, useState } from "react";
import { ActionIcon, Group, Tooltip } from "@mantine/core";
import MainTable from "../../../layouts/Table/MainTable";
import { IconEdit } from "@tabler/icons-react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { tagsColumn } from "../../../layouts/Table/columns/tagsColumn";
import { sharingColumn } from "../../../layouts/Table/columns/sharingColumn";
import ItemDeleter from "../../../components/TableComponents/ItemDeleter";
import { Link } from "react-router-dom";
import { useListingLoader } from "../../../components/hooks/ListingHooks";
import CreateModel from "./CreateModel";
import { getModelTypeProps } from "./modelUtils";
import numberColumn from "../../../layouts/Table/columns/numberColumn";
import ModelActions from "./actions/ModelActions";
import PageTitle from "../../../components/View/PageTitle";
import {
  getLinkedinInvitations,
  getLinkedinMessages,
} from "../../../api/ModelsApi";
import LinkedinTopMenu from "./linkedin/LinkedinTopMenu";

const ModelLinkedinListing = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);

  const columns = useMemo(
    () => [
      numberColumn(),
      nameColumn({
        parentPath: (row) => `/models/${row.modelType}/${row.id}`,
        withoutId: true,
        editFunction: (row) =>
          getModelTypeProps(row.modelType, false, row)?.editFunction,
        apiRef,
      }),
      {
        field: "modelType",
        headerName: "Type",
        width: 100,
        renderCell: ({ value, row }) => (
          <Group>
            {value === "linkedin-message" ? "Message" : "Invitation"}
          </Group>
        ),
      },
      tagsColumn({
        apiRef,
        editFunction: (row) =>
          getModelTypeProps(row.modelType, false, row)?.editFunction,
      }),
      sharingColumn({
        apiRef,
        editFunction: (row) =>
          getModelTypeProps(row.modelType, false, row)?.editFunction,
        allowResellerCustomers: true,
      }),
      dateColumn(),
    ],
    [apiRef],
  );

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [hasQuery, setHasQuery] = useState(false);
  const [highlight, setHighlight] = useState(null);

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <ModelActions current={row} setLoading={setLoading} />

          <Tooltip label={lang.global.edit}>
            <ActionIcon
              component={Link}
              to={`/models/${row.modelType}/${row.id}`}
              variant="subtle"
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>

          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={
              getModelTypeProps(row.modelType, false, row)?.deleteFunction
            }
            deletedMessage={lang.model_emails.delete_success}
            id={row.id}
            disabled={
              !row?.delete_permission && !row?.permission?.delete_permission
            }
            apiRef={apiRef}
          />
        </Group>
      );
    },
    [apiRef, lang.global.edit, lang.model_emails.delete_success],
  );

  const toolbarActions = useCallback(({ selectedIds }) => <></>, []);

  useListingLoader({
    loading,
    setLoading,
    listGetter: async function () {
      let invitations = await getLinkedinInvitations();
      let messages = await getLinkedinMessages();

      let result = [
        ...invitations.data.models_list.map((x) => ({
          ...x,
          modelType: "linkedin-invitation",
        })),
        ...messages.data.models_list.map((x) => ({
          ...x,
          modelType: "linkedin-message",
        })),
      ];

      return {
        data: {
          result,
        },
      };
    },
    responseField: "result",
    setRows,
    hasQuery,
  });

  return (
    <>
      <Group
        className="layout-block top"
        w={"100%"}
        gap={0}
        justify="space-between"
      >
        <PageTitle title={lang.header.navigations.models.linkedin} />

        <LinkedinTopMenu />

        <CreateModel
          isLinkedin
          handleTableLoad={(newId) => {
            setLoading(true);
          }}
        />
      </Group>

      <TableLayoutBlock
        folderType={"Model"}
        parentRoute={"/models"}
        getEditFunction={(item) =>
          getModelTypeProps(item?.modelType, false)?.editFunction
        }
        apiRef={apiRef}
        setTableLoading={setLoading}
      >
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows.filter((item) => (hasQuery ? true : !item.folder))}
          loading={loading}
          setLoading={setLoading}
          id={"model-table"}
          toolbarActions={toolbarActions}
          actionsCell={actionsCell}
          hasFolder
          noRowsDisplay={<CreateModel inTable isLinkedin />}
          defaultHighlight={highlight}
          setDefaultHighlight={setHighlight}
          setHasQuery={setHasQuery}
          hasQuery={hasQuery}
        />
      </TableLayoutBlock>
    </>
  );
};

export default ModelLinkedinListing;
